import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Community } from '@tennisup/contracts';

import { assetUrl } from '../lib/api';
import { WhatsAppIcon } from './icons';

/**
 * Una comunidad, como tarjeta.
 *
 * TODA LA TARJETA ES EL ENLACE, no solo el nombre. Con el nombre solo hay que
 * apuntarle a dos palabras, y en un teléfono eso es apuntarle a nada: la
 * tarjeta ocupa media pantalla y el 95 % de ella no hacía nada al tocarla.
 *
 * CÓMO SE HACE SIN ROMPER NADA. El enlace sigue siendo UNO SOLO, encima del
 * nombre, y se estira sobre toda la tarjeta con un `::after` posicionado
 * (`.community-card` es `position: relative` y el `::after` la cubre entera).
 * Eso es distinto de envolver todo en un `<a>`: envolviendo, el enlace se
 * llevaría adentro el enlace al grupo de WhatsApp —un enlace dentro de otro
 * no es HTML válido y los navegadores hacen cualquier cosa— y un lector de
 * pantalla leería la tarjeta entera como el texto del enlace, tres líneas de
 * descripción incluidas. Con el `::after`, lo que se anuncia sigue siendo el
 * nombre del club, y el enlace de WhatsApp se levanta por encima con
 * `position: relative` para que se pueda tocar.
 *
 * El estado solo se muestra cuando NO está aprobada. Poner «aprobada» en cada
 * tarjeta del listado sería ruido: si está en la lista, está aprobada.
 */
export function CommunityCard({ community }: { community: Community }): JSX.Element {
  const { t } = useTranslation();
  const logo = assetUrl(community.logoPath);

  return (
    <article className="community-card">
      {logo === null ? (
        <span className="community-logo community-logo-empty" aria-hidden="true">
          {community.name.trim().charAt(0).toUpperCase()}
        </span>
      ) : (
        <img className="community-logo" src={logo} alt="" />
      )}

      <div className="community-body">
        {/* EL GRUPO DE WHATSAPP VA PEGADO AL NOMBRE, no al final de la
            tarjeta. Suelto abajo se leía como si perteneciera a la
            descripción o a las etiquetas, que es lo último que se mira; al
            lado del nombre se entiende de una que el grupo es DE ESE CLUB. */}
        <div className="community-heading">
          <h3>
            <Link className="community-link" to={`/clubes/${community.id}`}>
              {community.name}
            </Link>
          </h3>

          {community.whatsappGroupUrl !== null && (
            // `noopener` no es opcional: sin él, la página que se abre puede
            // manipular la nuestra con `window.opener`.
            //
            // `community-over` lo levanta por encima del enlace estirado: sin
            // eso, tocar el ícono abriría la ficha del club.
            <a
              className="community-over community-whatsapp"
              href={community.whatsappGroupUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={t('communities.openGroup')}
            >
              {/* SOLO EL ÍCONO, pero NO mudo: el nombre viaja adentro del
                  `<svg>` como `<title>` y como `aria-label`, así que un lector
                  de pantalla sigue diciendo «Abrir el grupo de WhatsApp» y no
                  «enlace». Un ícono sin nombre es un botón que solo entiende
                  quien ya sabe qué hace. */}
              <WhatsAppIcon title={t('communities.openGroup')} />
            </a>
          )}
        </div>

        <p className="community-location">{community.locationText}</p>
        <p className="community-description">{community.description}</p>

        <p className="community-tags">
          {community.status !== 'APPROVED' && (
            <span className={`tag tag-${community.status.toLowerCase()}`}>
              {t(`communityStatus.${community.status}`)}
            </span>
          )}
          {/* Las solicitudes esperando van PRIMERO y en color: es lo único de
              la tarjeta que le pide algo a quien la mira. */}
          {community.pendingRequests > 0 && (
            <span className="tag tag-pending">
              {t('join.waiting', { count: community.pendingRequests })}
            </span>
          )}
          {community.myMembershipStatus === 'PENDING' && (
            <span className="tag tag-pending">{t('join.yourRequestPending')}</span>
          )}
          {community.myMembershipStatus === 'ACTIVE' && !community.isMine && (
            <span className="tag tag-ok">{t('join.youAreIn')}</span>
          )}
          {/* «PUEDE CREAR TORNEOS» YA NO ESTÁ, PARA NADIE.
              Es un permiso interno: a quien pasa mirando no le dice nada —los
              torneos, si existen, ya se ven— y a quien administra tampoco,
              porque en verde y presente solo confirma lo normal. Lo que sí
              hace falta es el caso contrario, y ese se avisa en la ficha del
              club: cuando FALTA el permiso y por eso no aparece el botón de
              crear torneo. */}

          {/* LAS CATEGORÍAS TAMPOCO. Un club que corre las doce llenaba la
              tarjeta de etiquetas iguales en todas las tarjetas, y lo que
              alguien busca al mirar la lista es de qué tamaño es el club. */}
          <span className="tag">{t('communities.memberCount', { count: community.memberCount })}</span>
        </p>

        {community.reviewNote !== null && (
          <p className="community-note">
            <strong>{t('communities.reviewNote')}:</strong> {community.reviewNote}
          </p>
        )}

      </div>
    </article>
  );
}
